import type { ReviewState } from '@fruuit/shared'
import { cn } from '@fruuit/ui'
import { Check } from 'lucide-react'

const STEPS = [
  { id: 'draft', label: 'Draft' },
  { id: 'in_review', label: 'In review' },
  { id: 'approved', label: 'Independent approval' },
  { id: 'published', label: 'Published client version' },
] as const

function stepIndex(state: ReviewState | 'pending_release' | null | undefined): number {
  switch (state) {
    case 'draft':
    case 'returned':
      return 0
    case 'in_review':
      return 1
    case 'approved':
      return 2
    case 'published':
    case 'retracted':
      return 3
    default:
      return -1
  }
}

/**
 * The S14 workflow strip: Draft → In review → Independent approval → Published client
 * version. With a `state`, the current step is highlighted and earlier ones ticked.
 */
export function WorkflowStrip({
  state,
  className,
}: {
  state?: ReviewState | 'pending_release' | null
  className?: string
}) {
  const current = stepIndex(state)
  return (
    <ol
      aria-label="Review workflow"
      className={cn('grid grid-cols-2 gap-2 sm:grid-cols-4', className)}
    >
      {STEPS.map((step, index) => {
        const done = current > index
        const active = current === index
        return (
          <li
            key={step.id}
            aria-current={active ? 'step' : undefined}
            className={cn(
              'flex items-center gap-2.5 rounded-md border px-3 py-2.5 text-sm',
              active
                ? 'border-teal bg-teal-50 font-semibold text-teal-800'
                : done
                  ? 'border-line bg-surface text-ink-soft'
                  : 'border-line bg-surface/60 text-sage',
              index === STEPS.length - 1 && !active && !done && 'border-dashed',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                active
                  ? 'bg-teal text-white'
                  : done
                    ? 'bg-positive-soft text-positive'
                    : 'bg-wash text-sage',
              )}
            >
              {done ? <Check className="size-3.5" /> : index + 1}
            </span>
            <span className="leading-tight">{step.label}</span>
          </li>
        )
      })}
    </ol>
  )
}
